import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { scrollToId } from "@/lib/scroll";
import { AppDownloadButton } from "./AppDownloadButton";
import { CtaButton } from "./CTA";
import { EASE, Eyebrow, MaskLines, Reveal } from "./Motion";
import { PhoneMockup } from "./PhoneMockup";
import { GlowOrb } from "./Glow";
import { SCREENS } from "@/config/screens";

const HeroPhones = ({ y }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const spring = { stiffness: 110, damping: 18 };
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), spring);
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), spring);

  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <motion.div style={{ y }} onPointerMove={move} onPointerLeave={reset} className="relative mx-auto h-[500px] w-full max-w-[440px] [perspective:1600px] sm:h-[600px]">
      <div className="absolute left-1/2 top-1/2 h-[380px] w-[380px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-pa-orange/25 blur-[110px]" aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, y: 120, rotateX: 25 }}
        animate={{ opacity: 1, y: 0, rotateX: 0 }}
        transition={{ duration: 1.6, ease: EASE, delay: 0.55 }}
        className="absolute inset-0"
      >
        <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative h-full w-full">
          <div className="absolute left-0 top-14 hidden opacity-70 sm:block" style={{ transform: "translateZ(-90px) rotate(-7deg)" }}>
            <PhoneMockup scale={0.86} glow={false} src={SCREENS.opportunities.src} label={SCREENS.opportunities.label} eager />
          </div>
          <div className="absolute inset-x-0 top-0 flex justify-center sm:justify-end" style={{ transform: "translateZ(50px)" }}>
            <PhoneMockup scale={0.9} testId="hero-phone" src={SCREENS.welcome.src} label={SCREENS.welcome.label} eager />
          </div>
          <div
            className="absolute bottom-16 left-0 hidden items-center gap-3 border border-white/15 bg-pa-surface/90 px-4 py-3 backdrop-blur-md sm:flex"
            style={{ transform: "translateZ(130px)" }}
            aria-hidden="true"
          >
            <span className="flex h-4 items-end gap-[3px]">
              {[0, 0.2, 0.4, 0.1].map((d) => <span key={d} className="h-full w-[3px] origin-bottom animate-eq bg-pa-orange" style={{ animationDelay: `${d}s` }} />)}
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-pa-ink">Pitch opened</span>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="grain relative overflow-hidden pb-20 pt-28 sm:pt-32 lg:pb-28 lg:pt-40">
      <div className="grid-lines pointer-events-none absolute inset-0" aria-hidden="true" />
      <GlowOrb className="-left-40 top-10 h-[520px] w-[520px]" strength="sm" />
      <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-6 lg:px-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <Reveal immediate delay={0.1}><Eyebrow>The music career app · iOS & Android</Eyebrow></Reveal>
          <MaskLines
            as="h1"
            immediate
            delay={0.2}
            label="Build your music career beyond the stream."
            className="mt-7 font-display uppercase leading-[0.86] text-pa-ink text-[clamp(3.7rem,15vw,6rem)] sm:text-[clamp(5rem,10vw,7.5rem)] lg:text-[clamp(5.5rem,8.2vw,9.25rem)]"
            lines={["Build your", "music career", <span className="text-glow text-pa-orange">Beyond the</span>, <>Stream<span className="text-glow text-pa-orange">.</span></>]}
          />
          <Reveal immediate delay={0.9} className="mt-8 max-w-lg text-base leading-relaxed text-pa-muted md:text-lg">
            Pitch, release, promote, analyze and grow — all from one platform built for the modern independent artist.
          </Reveal>
          <Reveal immediate delay={1.05} className="mt-10 flex flex-col gap-4 sm:flex-row">
            <AppDownloadButton source="hero" testId="hero-download-app-btn" />
            <CtaButton variant="ghost" icon={false} data-testid="hero-explore-btn" onClick={() => scrollToId("platform")}>
              Explore Pitchally
            </CtaButton>
          </Reveal>
        </motion.div>
        <div className="lg:col-span-5">
          <HeroPhones y={phoneY} />
        </div>
      </div>
    </section>
  );
};
